import { useI18N, usePageTitle, useResponsive } from "@sundae/react-hooks";
import { ExternalLink, Heading, Image, Link, TARGET_BLANK, Text } from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, useMemo } from "react";
import { MouseParallax } from "react-just-parallax";

import ConnectWalletButton from "../../../../components/ConnectWalletButton";
import PageContent from "../../../../components/PageContent/PageContent";
import { getPageTitle } from "../../../../constants/page.constants";
import { externalRoutes, paths as RoutePaths } from "../../../routes";

const HomeHero: FC = () => {
  const { t } = useI18N("home");
  const responsive = useResponsive();
  const isLargeScreen = useMemo(
    () => responsive.lg || responsive.xl || responsive.xxl,
    [responsive.lg, responsive.xl, responsive.xxl]
  );

  usePageTitle(getPageTitle(t("seo.home")));

  return (
    <PageContent
      className={cx(
        "relative mt-20 flex h-[calc(100vh-168px)] w-full flex-wrap",
        "md:h-full md:items-center",
        "lg:mt-0 lg:h-[calc(100vh-176px)]"
      )}
    >
      <div className="z-[2] flex-1 lg:p-7">
        <div className="flex w-full max-w-4xl flex-col gap-6 lg:gap-8">
          <Heading tag="h1" size={isLargeScreen ? "7xl" : "5xl"} weight="semibold">
            {t("hero.title")}
          </Heading>

          <Text tag="p" size="xl" className="w-full max-w-3xl" variant="muted">
            {t("hero.copy")}
          </Text>

          <ConnectWalletButton className="self-start" size="lg" />

          <div className="mt-5 flex w-full gap-6 md:gap-12">
            <Link size="lg" arrow="top-right" variant="muted" to={RoutePaths.liquidity.root}>
              {t("hero.links.explore")}
            </Link>
            <ExternalLink
              {...TARGET_BLANK}
              arrow="top-right"
              href={externalRoutes.discord}
              size="lg"
              variant="muted"
            >
              {t("discord", { ns: "glossary" })}
            </ExternalLink>
            <ExternalLink
              {...TARGET_BLANK}
              arrow="top-right"
              href={externalRoutes.faq}
              size="lg"
              variant="muted"
            >
              {t("faq", { ns: "glossary" })}
            </ExternalLink>
          </div>
        </div>
      </div>

      <div
        className={cx(
          "z-[1] h-full w-[133%] opacity-20 transition",
          "absolute left-1/2 right-0 -translate-x-1/2",
          "md:bottom-auto md:left-auto md:w-2/3 md:translate-x-0 md:opacity-25",
          "xl:w-2/4 xl:opacity-100",
          "xxl:w-2/3"
        )}
      >
        <MouseParallax strength={0.01}>
          <Image
            alt={t("home.disconnected", { ns: "alt-tags" })}
            className={cx(
              "absolute bottom-0",
              "md:bottom-auto md:right-0 md:top-1/2 md:-translate-y-1/2"
            )}
            height={1286}
            src="/static/images/home/not_connected/sundae_slide_v2_no_miscellaneous.png"
            width={1286}
          />
        </MouseParallax>
      </div>
    </PageContent>
  );
};

export default HomeHero;
